import React, { useEffect } from "react";
import { CContainer } from "@coreui/react-pro";
import PublicSiteShell from "../components/public/PublicSiteShell";
import "../styles/public-experience.css";

const values = [
  {
    title: "Fairness",
    text: "Wir stehen für faire Bedingungen, nachvollziehbare Abläufe und einen respektvollen Umgang mit allen Beteiligten. Internationale Rekrutierung soll auf Ausgewogenheit, Transparenz und Verantwortung beruhen.",
  },
  {
    title: "Respekt vor Qualifikation",
    text: "Vorhandene Abschlüsse, Berufserfahrung und Kompetenzen müssen ernst genommen und sauber bewertet werden. Unser Anspruch ist ein Umgang, der Qualifikation anerkennt und Entwicklung realistisch begleitet.",
  },
  {
    title: "Gute Vorbereitung",
    text: "Ein erfolgreicher beruflicher Start in Europa erfordert mehr als einen Arbeitsvertrag. Dazu gehören Kenntnisse über Bewerbung, Unternehmenskultur, Kommunikation, Arbeitsweise und Erwartungen im europäischen Arbeitsumfeld.",
  },
  {
    title: "Verantwortung",
    text: "Internationale Rekrutierung bringt Verantwortung mit sich. Diese betrifft nicht nur die Einstellung, sondern auch Onboarding, Orientierung, Integration und die praktische Begleitung in der Anfangsphase.",
  },
  {
    title: "Integration",
    text: "Nachhaltige Fachkräftegewinnung gelingt nur, wenn Menschen im Unternehmen und im neuen Umfeld tatsächlich ankommen können. Dazu gehören Willkommenskultur, klare Ansprechpartner, strukturierte Einarbeitung und ein integrationsorientiertes Umfeld.",
  },
  {
    title: "Verlässlichkeit",
    text: "Menschen, die in einem neuen Land beruflich neu beginnen, brauchen klare Informationen, belastbare Zusagen und nachvollziehbare Prozesse. Verlässlichkeit ist deshalb ein zentraler Maßstab unserer Arbeit.",
  },
  {
    title: "Lebensnahe Umsetzung",
    text: "Ein tragfähiger Start in Europa setzt voraus, dass auch die praktische Lebensrealität berücksichtigt wird. Dazu zählen insbesondere die ersten Wochen nach Ankunft, finanzielle Übergangsphasen sowie die Frage nach Wohnraum oder konkreter Unterstützung bei der Wohnraumsuche.",
  },
  {
    title: "Nachhaltigkeit",
    text: "Unser Ziel ist nicht die kurzfristige Besetzung offener Stellen, sondern eine stabile Verbindung zwischen Fachkraft, Arbeitgeber und neuem Lebensumfeld. Nachhaltigkeit bedeutet für uns Passung, Vorbereitung und langfristige Perspektive.",
  },
];

export default function Leitbild() {
  useEffect(() => {
    document.title = "Leitbild | Experts4Europe";
  }, []);

  const hero = (
    <section className="public-guiding-hero">
      <CContainer fluid className="public-core-container public-guiding-shell public-page-shell">
        <div className="public-guiding-hero__panel public-page-hero-panel">
          <div className="public-guiding-hero__copy">
            <span className="public-core-eyebrow public-page-overline">Leitbild</span>
            <h1 className="public-page-title">
              <span className="public-guiding-hero__title-line">Verantwortungsvolle Wege für Fachkräfte</span>
              <span className="public-guiding-hero__title-line">und Arbeitgeber in Europa</span>
            </h1>
            <p className="public-guiding-hero__lead public-page-body">
              Experts4Europe steht für faire, vorbereitete und verantwortungsvolle internationale Fachkräftegewinnung in Europa.
            </p>
          </div>

          <aside className="public-guiding-hero__meta">
            <div className="public-guiding-callout public-page-callout">
              <strong>Mission</strong>
              <span>Faire, gut vorbereitete und verantwortungsvolle Wege qualifizierter Fachkräfte aus Drittstaaten in den europäischen Arbeitsmarkt.</span>
            </div>
            <div className="public-guiding-callout public-page-callout">
              <strong>Vision</strong>
              <span>Internationale Fachkräftegewinnung in Europa transparenter, fairer und tragfähiger gestalten.</span>
            </div>
            <div className="public-guiding-callout public-page-callout">
              <strong>Kurzfassung</strong>
              <span>Im Mittelpunkt stehen faire Bedingungen, gute Vorbereitung, Respekt vor Qualifikation, echte Integrationsbereitschaft und tragfähige Perspektiven für beide Seiten.</span>
            </div>
          </aside>
        </div>
      </CContainer>
    </section>
  );

  return (
    <PublicSiteShell
      pageClassName="public-guiding-page public-guiding-page--leitbild"
      hero={hero}
    >
      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-guiding-shell public-page-shell">
          <div className="public-guiding-stack">
            <article className="public-guiding-card public-guiding-card--cool public-page-card">
              <span className="public-core-eyebrow public-page-overline">Unsere Mission</span>
              <div className="public-guiding-richtext public-page-richtext">
                <p>Experts4Europe steht für faire, gut vorbereitete und verantwortungsvolle Wege qualifizierter Fachkräfte aus Drittstaaten in den europäischen Arbeitsmarkt.</p>
                <p>Wir unterstützen qualifizierte Menschen dabei, ihre beruflichen Perspektiven in Europa strukturiert und realistisch zu entwickeln. Gleichzeitig begleiten wir Arbeitgeber bei einer internationalen Rekrutierung, die nicht nur auf Besetzung, sondern auf nachhaltige Integration ausgerichtet ist.</p>
                <p>Für uns umfasst dieser Weg mehr als die Vermittlung zwischen Bewerber und Arbeitgeber. Entscheidend sind auch die fachliche Einordnung vorhandener Qualifikationen, die Vorbereitung auf europäische Bewerbungs- und Arbeitsstandards, die soziale und betriebliche Integration sowie verlässliche Rahmenbedingungen für das Ankommen im neuen Umfeld.</p>
              </div>
            </article>

            <article className="public-guiding-card public-guiding-card--warm public-page-card">
              <span className="public-core-eyebrow public-page-overline">Unsere Vision</span>
              <div className="public-guiding-richtext public-page-richtext">
                <p>Wir möchten dazu beitragen, dass internationale Fachkräftegewinnung in Europa transparenter, fairer und tragfähiger gestaltet wird.</p>
                <p>Qualifizierte Migration soll von Klarheit, Verlässlichkeit und gegenseitigem Respekt geprägt sein. Arbeitgeber sollen internationale Rekrutierung nicht als isolierten Beschaffungsprozess verstehen, sondern als verantwortungsvollen Gesamtprozess, der fachliche, menschliche und praktische Aspekte gleichermaßen berücksichtigt.</p>
              </div>
            </article>

            <article className="public-guiding-card public-page-card">
              <span className="public-core-eyebrow public-page-overline">Unsere Werte</span>
              <div className="public-guiding-values-grid">
                {values.map((item, index) => (
                  <section
                    key={item.title}
                    className={`public-guiding-value public-page-value-card ${
                      index < 2 || (index >= 4 && index < 6)
                        ? "public-guiding-value--cool"
                        : "public-guiding-value--warm"
                    }`}
                  >
                    <h2 className="public-page-subtitle">{item.title}</h2>
                    <p className="public-page-body">{item.text}</p>
                  </section>
                ))}
              </div>
            </article>
          </div>
        </CContainer>
      </section>
    </PublicSiteShell>
  );
}
